import { Injectable, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ThemeStateService {
  readonly darkMode = signal(false);

  initializeFromDom(): void {
    const html = document.documentElement;
    // `data-color-scheme` on <html> is the only hook for the dark scheme: Optimus
    // (`darkModeSelector`), Tailwind's `dark` variant and every `--stela-*` dark token read it.
    const appliedScheme = html.getAttribute('data-color-scheme');
    if (appliedScheme) {
      this.darkMode.set(appliedScheme === 'dark');
      return;
    }
    // Nothing applied yet: routes outside LayoutComponent never resolve the saved preference, so
    // restore it here without re-persisting it.
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'skin-3') {
      this.setDarkMode(savedTheme === 'dark', { persist: false });
      return;
    }
    this.darkMode.set(false);
  }

  /**
   * Single owner of the dark/light state: sets `data-color-scheme` on `<html>` (the one DOM hook
   * the app reads) and persists the user's choice.
   *
   * `persist: false` is for DOM writes that must NOT count as an explicit user choice — the initial
   * resolution from the OS preference, and the OS `change` listener while no preference is saved —
   * so a later reload still follows the OS instead of getting stuck on whatever it was at that
   * moment. `toggleDarkMode()` (the user flipping the switch) always persists.
   */
  setDarkMode(isDark: boolean, options: { persist?: boolean } = {}): void {
    const persist = options.persist ?? true;
    document.documentElement.setAttribute('data-color-scheme', isDark ? 'dark' : 'light');

    if (persist) {
      localStorage.setItem('theme', isDark ? 'dark' : 'skin-3');
    }

    this.darkMode.set(isDark);
  }

  toggleDarkMode(): void {
    this.setDarkMode(!this.darkMode());
  }
}
